import { useMemo, useState } from 'react';
import { useParticipants, useLocalParticipant } from '@livekit/components-react';
import type { StageDisplayMode } from '@modelcast/types';
import { useRoom } from './RoomProvider';
import { HostBrowserAudioControl } from '../audio/HostBrowserAudioControl';

interface HostControlsPanelProps {
  onOpenSession?: () => void;
  onEndSession?: () => void;
  hasSessionStarted?: boolean;
  isEndingSession?: boolean;
  variant?: 'default' | 'mobileTab';
}

export function HostControlsPanel({
  onOpenSession,
  onEndSession,
  hasSessionStarted,
  isEndingSession = false,
  variant = 'default',
}: HostControlsPanelProps) {
  const participants = useParticipants();
  const { localParticipant } = useLocalParticipant();
  const {
    isHost,
    modelParticipantId,
    modelProfileId,
    modelParticipantIds,
    hostParticipantIds,
    originalHostId,
    participants: sessionParticipants,
    bannedParticipants,
    stageDisplayMode,
    setModelProfile,
    setStageDisplayMode,
    toggleHost,
    unbanParticipant,
  } = useRoom();

  const [showHostDropdown, setShowHostDropdown] = useState(false);
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [unbanningInviteId, setUnbanningInviteId] = useState<string | null>(null);
  const isMobileTab = variant === 'mobileTab';

  const currentSessionParticipant = sessionParticipants.find(
    participant => participant.participantId === localParticipant?.identity
  );
  const isCurrentUserModel = currentSessionParticipant?.role === 'model' ||
    (!!localParticipant?.identity && modelParticipantIds.includes(localParticipant.identity));
  const canChangeModel = isHost || isCurrentUserModel;

  const admittedParticipants = participants;
  const sessionParticipantById = useMemo(() => (
    new Map(sessionParticipants.map(participant => [participant.participantId, participant]))
  ), [sessionParticipants]);
  const modelProfileOptions = useMemo(() => {
    const options = new Map<string, {
      profileId: string;
      displayName: string;
      cameraCount: number;
      isLocal: boolean;
    }>();

    for (const participant of admittedParticipants) {
      const sessionParticipant = sessionParticipantById.get(participant.identity);
      const profileId = sessionParticipant?.profileId;
      if (!profileId) continue;

      const existing = options.get(profileId);
      if (existing) {
        existing.cameraCount += 1;
        existing.isLocal ||= participant.isLocal;
        continue;
      }

      options.set(profileId, {
        profileId,
        displayName: sessionParticipant?.displayName || participant.name || participant.identity,
        cameraCount: 1,
        isLocal: participant.isLocal,
      });
    }

    return Array.from(options.values()).sort((a, b) =>
      a.displayName.localeCompare(b.displayName)
    );
  }, [admittedParticipants, sessionParticipantById]);

  // Get display name for a participant
  const getDisplayName = (identity: string) => {
    const participant = admittedParticipants.find(p => p.identity === identity);
    const sessionParticipant = sessionParticipantById.get(identity);
    if (sessionParticipant?.displayName) return sessionParticipant.displayName;
    if (!participant) return 'Unknown';
    return participant.name || participant.identity;
  };

  // Get host names for display
  const hostNames = hostParticipantIds
    .map(getDisplayName)
    .slice(0, 2);
  const hostDisplayText = hostParticipantIds.length === 0
    ? 'None selected'
    : hostParticipantIds.length <= 2
      ? hostNames.join(', ')
      : `${hostNames.join(', ')} +${hostParticipantIds.length - 2}`;

  // Get model name for display
  const activeModelProfileOption = modelProfileOptions.find(option => option.profileId === modelProfileId);
  const modelDisplayText = activeModelProfileOption
    ? `${activeModelProfileOption.displayName}${activeModelProfileOption.cameraCount > 1 ? ` +${activeModelProfileOption.cameraCount - 1} camera${activeModelProfileOption.cameraCount > 2 ? 's' : ''}` : ''}`
    : modelProfileId
      ? 'Selected model'
      : modelParticipantId
        ? getDisplayName(modelParticipantId)
        : 'None selected';

  const formatBannedAt = (bannedAt: string) => {
    const date = new Date(bannedAt);
    if (Number.isNaN(date.getTime())) return '';

    return date.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const handleUnbanParticipant = (inviteId: string) => {
    setUnbanningInviteId(inviteId);
    unbanParticipant(inviteId, (response) => {
      setUnbanningInviteId(current => current === inviteId ? null : current);
      if (!response.success) {
        alert(response.error || 'Failed to allow this participant back in');
      }
    });
  };
  const displayModeOptions: { value: StageDisplayMode; label: string }[] = [
    { value: 'grid', label: 'Grid' },
    { value: 'active-speaker', label: 'Active Speaker' },
  ];

  // Don't render if user can't control anything and is not the current model.
  if (!isHost && !isCurrentUserModel) {
    return null;
  }

  const renderEndSessionButton = () => {
    if (!isHost || !hasSessionStarted || !onEndSession) return null;

    return (
      <button
        onClick={onEndSession}
        disabled={isEndingSession}
        className="w-full btn bg-warn hover:bg-warn/90 text-white font-semibold py-2.5 rounded-lg shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {isEndingSession ? 'Ending...' : 'End Session'}
      </button>
    );
  };

  return (
    <div className={`${isMobileTab ? 'flex flex-1 min-h-[360px] flex-col' : 'flex-shrink-0'} px-3 py-3 border-t border-b border-line lg:border-t-0`}>
      <div className="font-mono text-[10px] text-text-faint uppercase mb-3">
        Session Controls
      </div>

      <div className="space-y-2">
        {/* Open Session button - only for host before session starts */}
        {isHost && !hasSessionStarted && onOpenSession && (
          <div>
            <button
              onClick={onOpenSession}
              className="w-full btn bg-ok hover:bg-ok/90 text-white font-semibold py-2.5 rounded-lg shadow-sm"
            >
              Open Session
            </button>
            <p className="mt-1.5 text-[11px] leading-snug text-text-dim">
              Allow ticket holders to enter the session
            </p>
          </div>
        )}
        {!isMobileTab && renderEndSessionButton()}
        {isHost && <HostBrowserAudioControl />}

        <div>
          <label className="block text-[10px] text-text-dim mb-1">Display Mode</label>
          <div className="grid grid-cols-2 gap-1 rounded-lg border border-line bg-panel p-1">
            {displayModeOptions.map((option) => {
              const isSelected = stageDisplayMode === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setStageDisplayMode(option.value)}
                  aria-pressed={isSelected}
                  className={`rounded-md px-2 py-1.5 text-[12px] font-semibold transition-colors ${
                    isSelected
                      ? 'bg-text text-bg shadow-sm'
                      : 'text-text-dim hover:bg-panel2 hover:text-text'
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Model selector */}
        <div className="relative">
          <label className="block text-[10px] text-text-dim mb-1">Model</label>
          <button
            onClick={() => canChangeModel && setShowModelDropdown(!showModelDropdown)}
            disabled={!canChangeModel}
            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border transition-colors text-left ${
              canChangeModel
                ? 'bg-panel2 border-line hover:border-tag/50 cursor-pointer'
                : 'bg-panel border-line/50 cursor-default opacity-60'
            }`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-2 h-2 rounded-full bg-tag flex-shrink-0" />
              <span className="text-[12px] text-text truncate">{modelDisplayText}</span>
            </div>
            {canChangeModel && (
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                className={`text-text-faint flex-shrink-0 transition-transform ${showModelDropdown ? 'rotate-180' : ''}`}
              >
                <path d="M2 4 L6 8 L10 4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </button>

          {/* Model dropdown */}
          {canChangeModel && showModelDropdown && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowModelDropdown(false)} />
              <div className="absolute left-0 right-0 bottom-full mb-1 z-50 py-1 bg-panel border border-line rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {/* No model option */}
                <button
                  className={`w-full px-3 py-2 flex items-center gap-2 text-left hover:bg-panel2 transition-colors ${
                    !modelProfileId ? 'bg-tag/10' : ''
                  }`}
                  onClick={() => {
                    setModelProfile(null);
                    setShowModelDropdown(false);
                  }}
                >
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${!modelProfileId ? 'bg-tag' : 'bg-line'}`} />
                  <span className="text-[12px] text-text-dim flex-1">No model</span>
                  {!modelProfileId && (
                    <span className="text-[9px] font-mono text-tag uppercase">Current</span>
                  )}
                </button>
                {modelProfileOptions.length === 0 ? (
                  <div className="px-3 py-2 text-[11px] text-text-faint">No participants</div>
                ) : (
                  modelProfileOptions.map((option) => {
                    const isModel = option.profileId === modelProfileId;
                    return (
                      <button
                        key={option.profileId}
                        className={`w-full px-3 py-2 flex items-center gap-2 text-left hover:bg-panel2 transition-colors ${
                          isModel ? 'bg-tag/10' : ''
                        }`}
                        onClick={() => {
                          setModelProfile(option.profileId);
                          setShowModelDropdown(false);
                        }}
                      >
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isModel ? 'bg-tag' : 'bg-line'}`} />
                        <span className="text-[12px] text-text flex-1 truncate">
                          {option.isLocal ? `${option.displayName} (You)` : option.displayName}
                          {option.cameraCount > 1 ? ` · ${option.cameraCount} cameras` : ''}
                        </span>
                        {isModel && (
                          <span className="text-[9px] font-mono text-tag uppercase">Current</span>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>

        {/* Host selector - only for hosts */}
        {isHost && (
          <div className="relative">
            <label className="block text-[10px] text-text-dim mb-1">Session controls access</label>
            <button
              onClick={() => setShowHostDropdown(!showHostDropdown)}
              className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-panel2 border border-line hover:border-accent/50 transition-colors text-left"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-2 h-2 rounded-full bg-accent flex-shrink-0" />
                <span className="text-[12px] text-text truncate">{hostDisplayText}</span>
              </div>
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                className={`text-text-faint flex-shrink-0 transition-transform ${showHostDropdown ? 'rotate-180' : ''}`}
              >
                <path d="M2 4 L6 8 L10 4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            {/* Host dropdown */}
            {showHostDropdown && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowHostDropdown(false)} />
                <div className="absolute left-0 right-0 bottom-full mb-1 z-50 py-1 bg-panel border border-line rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  <div className="px-3 py-1.5 text-[10px] text-text-faint border-b border-line">
                    Select multiple co-organizers
                  </div>
                  {admittedParticipants.length === 0 ? (
                    <div className="px-3 py-2 text-[11px] text-text-faint">No participants</div>
                  ) : (
                    admittedParticipants.map((participant) => {
                      const isCurrentHost = hostParticipantIds.includes(participant.identity);
                      const isOriginalHost = participant.identity === originalHostId;
                      const isLocal = participant.isLocal;
                      const name = participant.name || participant.identity;
                      return (
                        <button
                          key={participant.identity}
                          className={`w-full px-3 py-2 flex items-center gap-2 text-left transition-colors ${
                            isCurrentHost ? 'bg-accent/10 hover:bg-accent/15' : 'hover:bg-panel2'
                          } ${isOriginalHost ? 'cursor-default' : 'cursor-pointer'}`}
                          onClick={() => {
                            if (!isOriginalHost) {
                              toggleHost(participant.identity);
                            }
                          }}
                          disabled={isOriginalHost}
                        >
                          <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                            isCurrentHost ? 'bg-accent border-accent text-white' : 'border-line bg-panel'
                          }`}>
                            {isCurrentHost && (
                              <svg width="10" height="10" viewBox="0 0 12 12">
                                <path d="M2 6 L5 9 L10 3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </div>
                          <span className="text-[12px] text-text flex-1 truncate">
                            {isLocal ? `${name} (You)` : name}
                          </span>
                          {isOriginalHost && (
                            <span className="text-[9px] font-mono text-accent uppercase">Creator</span>
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
      {(isHost || isCurrentUserModel) && bannedParticipants.length > 0 && (
        <div className="mt-3 rounded-lg border border-warn/25 bg-warn/5 p-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="font-mono text-[10px] uppercase text-warn">Banned from this event</div>
            <div className="text-[11px] text-text-dim">{bannedParticipants.length}</div>
          </div>
          <div className="space-y-2">
            {bannedParticipants.map((banned) => (
              <div key={banned.inviteId} className="flex items-center gap-2 rounded border border-line bg-panel px-2 py-2">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[12px] text-text">{banned.displayName || 'Removed participant'}</div>
                  <div className="text-[10px] text-text-faint">
                    Removed and blocked{formatBannedAt(banned.bannedAt) ? ` at ${formatBannedAt(banned.bannedAt)}` : ''}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleUnbanParticipant(banned.inviteId)}
                  disabled={unbanningInviteId === banned.inviteId}
                  className="rounded border border-line bg-panel2 px-2.5 py-1.5 text-[11px] font-semibold text-text transition-colors hover:border-ok/50 hover:text-ok disabled:cursor-wait disabled:opacity-60"
                >
                  {unbanningInviteId === banned.inviteId ? 'Allowing...' : 'Allow back in'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      {isMobileTab && isHost && hasSessionStarted && onEndSession && (
        <div className="mt-auto pt-4">
          {renderEndSessionButton()}
        </div>
      )}
    </div>
  );
}
